/**
 * Deferred Fetch Utility
 *
 * Defers non-critical data fetches until after first paint to improve LCP.
 * Uses requestIdleCallback with setTimeout fallback for Safari.
 */

/**
 * Defer a callback until the browser is idle (after first paint)
 * Falls back to setTimeout if requestIdleCallback is not supported (Safari)
 *
 * @param callback - Function to call when idle
 * @param timeout - Max timeout in ms (default 2000ms)
 */
export function runWhenIdle(callback: () => void, timeout = 2000): void {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(callback, { timeout });
  } else {
    // Fallback for Safari
    setTimeout(callback, 1);
  }
}

/**
 * Session storage key prefix for per-user deferred fetches
 */
const DEFERRED_FETCH_PREFIX = 'deferred_fetch';

/**
 * Check if a specific deferred fetch has already run for this user
 * @param resource - Resource type (e.g., 'lessons', 'usage', 'profile')
 * @param userId - User ID
 */
export function hasDeferredFetchRun(resource: string, userId: string): boolean {
  try {
    const key = `${DEFERRED_FETCH_PREFIX}:${resource}:${userId}`;
    return sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

/**
 * Mark a specific deferred fetch as completed for this user
 * @param resource - Resource type (e.g., 'lessons', 'usage', 'profile')
 * @param userId - User ID
 */
export function markDeferredFetchComplete(resource: string, userId: string): void {
  try {
    const key = `${DEFERRED_FETCH_PREFIX}:${resource}:${userId}`;
    sessionStorage.setItem(key, 'true');
  } catch {
    // Ignore if sessionStorage is not available
  }
}

/**
 * Clear all deferred fetch markers for a user (useful on logout)
 * @param userId - User ID
 */
export function clearDeferredFetches(userId: string): void {
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(`${DEFERRED_FETCH_PREFIX}:`) && key.endsWith(`:${userId}`)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch {
    // Ignore if sessionStorage is not available
  }
}

// DEPRECATED: Keep for backwards compatibility, will be removed
export function haveDeferredFetchesRun(): boolean {
  return false; // Always return false to force migration to new API
}

export function markDeferredFetchesComplete(): void {
  // No-op, deprecated
}
